<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useLocale } from '~/composables/useLocale'

const { t } = useI18n()
const { getCurrentLocale, setLocale, availableLocales } = useLocale()
const showLanguages = ref(false)
const languageContainer = ref<HTMLElement | null>(null)
const currentLocale = computed(() => getCurrentLocale())

const emit = defineEmits(['close', 'logout'])

const handleClose = () => {
  emit('close')
}

const handleLogout = () => {
  emit('logout')
}

const switchLanguage = (code: string) => {
  if (setLocale(code)) {
    showLanguages.value = false
    emit('close')
  }
}

// 处理点击事件
const handleClickOutside = (event: MouseEvent) => {
  const container = languageContainer.value
  if (container instanceof HTMLElement && event.target instanceof Node && !container.contains(event.target)) {
    showLanguages.value = false;
  }
}

// 组件挂载时添加点击事件监听
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
})

// 组件卸载时移除点击事件监听
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
})

// 阻止弹窗内的点击事件冒泡到遮罩层
const stopPropagation = (event: Event) => {
  event.stopPropagation()
}
</script>

<template>
  <div>
    <!-- 遮罩层 -->
    <div class="overlay" @click="handleClose" />
    
    <div class="settings-popup" @click="stopPropagation">
      <div class="settings-content">
        <NuxtLink to="/billing" class="settings-item item-content" @click="handleClose">
          <div class="i-lucide-credit-card text-xl" aria-hidden="true" />
          <span class="item-text">{{ t('billing.membershipEntry') }}</span>
        </NuxtLink>
        <div 
          ref="languageContainer"
          class="settings-item"
          @click="showLanguages = !showLanguages"
        >
          <div class="item-content">
            <div class="i-lucide-languages text-xl" />
            <span class="item-text">{{ t('settings.language') }}</span>
            <div class="i-lucide-chevron-right ml-auto text-lg" />
          </div>
          
          <!-- 语言选择弹窗 -->
          <div 
            v-if="showLanguages" 
            class="language-popup"
          >
            <button
              v-for="lang in availableLocales"
              :key="lang.code"
              class="language-item"
              :class="{ 'active': currentLocale === lang.code }"
              @click.stop="switchLanguage(lang.code)"
            >
              <span class="language-name">{{ lang.name }}</span>
              <div v-if="currentLocale === lang.code" class="i-lucide-check text-blue-500" />
            </button>
          </div>
        </div>

        <div class="settings-item" @click="handleLogout">
          <div class="item-content text-red-500">
            <div class="i-lucide-log-out text-xl" />
            <span class="item-text">{{ t('settings.logout') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.overlay {
  @apply fixed inset-0 z-30;
}

.settings-popup {
  @apply fixed bg-white dark:bg-gray-800 rounded-xl shadow-lg z-40;
  @apply w-[200px] overflow-visible;
  @apply border border-gray-200 dark:border-gray-700;
  /* 位置调整：固定在左侧导航栏内，在用户信息上方 */
  @apply lg:absolute lg:bottom-[80px] lg:left-4 lg:right-4;
}

.settings-content {
  @apply py-2;
}

.settings-item {
  @apply relative;
}

.item-content {
  @apply flex items-center gap-3 px-4 py-3;
  @apply hover:bg-gray-100 dark:hover:bg-gray-700;
  @apply cursor-pointer transition-colors duration-200;
}

.item-text {
  @apply text-sm font-medium text-gray-700 dark:text-gray-300;
}

.language-popup {
  @apply absolute bottom-full left-0 mb-1;
  @apply w-[200px] bg-white dark:bg-gray-800 rounded-xl shadow-lg;
  @apply border border-gray-200 dark:border-gray-700;
  @apply py-2;
  @apply max-h-[300px] overflow-y-auto;
  @apply z-50;
}

.language-item {
  @apply flex items-center justify-between w-full px-3 py-2;
  @apply text-gray-700 dark:text-gray-300;
  @apply hover:bg-gray-100 dark:hover:bg-gray-700;
  @apply transition-colors duration-200;
}

.language-item.active {
  @apply bg-gray-50 dark:bg-gray-700;
}

.language-name {
  @apply text-sm;
}
</style>
